import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiPlus, FiSearch, FiUser } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite } from '../utilities/adminAccess';
import { champFields } from '../utilities/tournamentsApi';
import {
  PLAYERS_PAGE_SIZE,
  extractPlayersPage,
  getPlayerPhotoUrl,
  getTeamPhotoUrl,
  isPlayerCaptain,
  playerCardPath,
  playerDisplayName,
  playerPositionLabel,
  uniquePlayerTeams,
} from '../utilities/playersApi';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import defaultPlayerImage from '../images/defaultAvatar.png';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/playersManagement.css';
import '../styles/playerDuplicates.css';

export default function PlayersManagement() {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { champId, selected: championship, loading: champsLoading } = useChampionships();

  const [scopeAll, setScopeAll] = useState(false);
  const [players, setPlayers] = useState([]);
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const searchTimer = useRef(null);
  const requestIdRef = useRef(0);
  const playersRef = useRef([]);
  playersRef.current = players;

  const filterChampId = scopeAll ? null : champId;

  useEffect(() => {
    // Без «все» ждём чемпионат из шапки; в режиме «все» грузим сразу
    if (!scopeAll && !champId && champsLoading) return undefined;
    if (!scopeAll && !champId && !champsLoading) {
      setPlayers([]);
      setTotal(null);
      setHasMore(false);
      setLoading(false);
      setMessage({ text: t('dupNeedChamp'), type: 'error' });
      return undefined;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const delay = searchQuery ? 350 : 0;
    searchTimer.current = setTimeout(() => {
      loadPlayers(searchQuery, 0, false);
    }, delay);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery, scopeAll, champId, champsLoading]);

  const loadPlayers = async (search, offset, append) => {
    const requestId = ++requestIdRef.current;
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setMessage({ text: '', type: '' });
    }

    try {
      const body = {
        ...(filterChampId ? champFields(filterChampId) : {}),
        limit: PLAYERS_PAGE_SIZE,
        offset,
      };
      if (search && search.trim()) body.search = search.trim();

      const response = await authorizedPost(dispatch, API_URLS.getAdminPlayers, body);
      if (requestId !== requestIdRef.current) return;

      const page = extractPlayersPage(response.data);
      const nextPlayers = append ? playersRef.current.concat(page.items) : page.items;
      setPlayers(nextPlayers);
      setTotal(page.total);
      setHasMore(
        page.total != null
          ? nextPlayers.length < page.total
          : page.items.length === PLAYERS_PAGE_SIZE
      );
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      if (!append) {
        setPlayers([]);
        setTotal(null);
        setHasMore(false);
      }
      setMessage({ text: getApiErrorMessage(error, t('pmSearchError')), type: 'error' });
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const columns = useMemo(() => {
    const cols = [
      {
        key: 'family',
        label: t('tmPlayer'),
        className: 'st-cell-name',
        render: (item) => {
          const teams = uniquePlayerTeams(item.teams);
          const teamNames = teams.map((team) => team.team_name || team.name).filter(Boolean);
          const position = playerPositionLabel(item) || '';
          const champName = item.championat_name || item.championship_name || '';
          const meta = [
            position,
            scopeAll && champName ? champName : null,
            teamNames.join(', '),
          ].filter(Boolean).join(' · ');
          return (
            <span className="tr-name pm-player-main">
              <img
                src={getPlayerPhotoUrl(item.image || item.photo || item.avatar) || defaultPlayerImage}
                alt=""
                className="pm-list-photo"
                onError={(event) => { event.currentTarget.src = defaultPlayerImage; }}
              />
              <span className="pm-player-text">
                <span className="pm-player-name">
                  {[item.family, item.name].filter(Boolean).join(' ') || playerDisplayName(item) || '—'}
                </span>
                {meta ? <small className="pm-player-meta">{meta}</small> : null}
              </span>
            </span>
          );
        },
      },
      {
        key: 'teams',
        label: t('plTeamsBlock'),
        className: 'pm-cell-teams',
        render: (item) => {
          const teams = uniquePlayerTeams(item.teams);
          if (!teams.length) return '—';
          return (
            <span className="pm-list-teams">
              {teams.map((team) => (
                <span key={team.team_id || team.id || team.team_name} className="pm-list-team">
                  <img
                    src={getTeamPhotoUrl(team.team_image || team.image) || defaultTeamLogo}
                    alt=""
                    onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                  />
                  <span>{team.team_name || team.name || '—'}</span>
                  {isPlayerCaptain(team, item) ? (
                    <span className="pl-captain-badge">{t('plCaptain')}</span>
                  ) : null}
                </span>
              ))}
            </span>
          );
        },
      },
    ];
    if (scopeAll) {
      cols.push({
        key: 'championat',
        label: t('pmChampionship'),
        className: 'pm-cell-champ',
        render: (item) => item.championat_name || item.championship_name || '—',
      });
    }
    cols.push({
      key: 'position',
      label: t('pmPosition'),
      className: 'pm-cell-pos',
      render: (item) => playerPositionLabel(item) || '—',
    });
    return cols;
  }, [scopeAll, t]);

  const foundCount = total != null ? total : players.length;

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page pm-list-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('navPlayers') }]} />
          </div>
        </div>

        <div className="st-toolbar">
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              type="text"
              className="st-search-input"
              placeholder={t('pmSearchPh')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery ? (
              <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                ×
              </button>
            ) : null}
          </div>
          <div className="dup-scope pm-scope">
            <span className="dup-fields-label">{t('dupScope')}</span>
            <label className="st-check">
              <input
                type="radio"
                name="pm-scope"
                checked={scopeAll}
                onChange={() => setScopeAll(true)}
              />
              {t('dupScopeAll')}
            </label>
            <label className={`st-check${!champId ? ' is-disabled' : ''}`}>
              <input
                type="radio"
                name="pm-scope"
                checked={!scopeAll}
                disabled={!champId}
                onChange={() => setScopeAll(false)}
              />
              {championship?.name
                ? t('dupScopeChampNamed', { name: championship.name })
                : t('dupScopeChamp')}
            </label>
          </div>
          <div className="st-meta">
            <strong>{t('pmFound', { count: foundCount })}</strong>
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-content">
            <div className="st-block">
              <h2 className="st-block-header">{t('navPlayers')}</h2>
              {loading ? (
                <div className="st-loading">
                  <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                  <p>{t('ttLoadingPlayers')}</p>
                </div>
              ) : (
                <>
                  <ReorderableTable
                    tableClassName={`st-table-players${scopeAll ? ' is-all-champs' : ''}`}
                    columns={columns}
                    items={players}
                    getRowKey={(item, index) => item.id || item.player_id || index}
                    reorderMode={false}
                    getRowHref={(item) => {
                      const id = item.id || item.player_id;
                      return id ? playerCardPath(id) : null;
                    }}
                    empty={(
                      <div className="st-empty">
                        <FiUser size={48} />
                        <h3>{t('pmNoResults')}</h3>
                        <p>{t('pmNoResultsHint')}</p>
                      </div>
                    )}
                  />
                  {hasMore ? (
                    <div className="pm-load-more">
                      <button
                        type="button"
                        className="st-btn st-btn-secondary"
                        disabled={loadingMore}
                        onClick={() => loadPlayers(searchQuery, players.length, true)}
                      >
                        {loadingMore ? t('ttLoadingPlayers') : t('dqLoadMore')}
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="st-actions">
          <IfCanWrite entity="players">
            <Link
              to="/players_management/new"
              className={`st-btn st-btn-primary${loading ? ' is-disabled' : ''}`}
              aria-disabled={loading || undefined}
              onClick={(event) => { if (loading) event.preventDefault(); }}
            >
              <FiPlus />
              <span className="tr-btn-full">{t('plCreatePlayer')}</span>
              <span className="tr-btn-short">{t('plCreatePlayerShort')}</span>
            </Link>
          </IfCanWrite>
        </div>
      </div>
    </div>
  );
}
